import { useEffect } from 'react';

import posthog from 'posthog-js';

import { addListener, removeListener } from '@cast/analytics';

import {
  isPostHogEnabled,
  isPostHogInitEnabled,
  resolvePostHogToken,
} from './isPostHogEnabled';

const listenerId = 'post-hog';

type PostHogAnalyticsUser = {
  email: string;
};

type PostHogAnalyticsOrganization = {
  id: string;
  name: string;
};

type UseEmitPostHogEventsArgs = {
  appName?: string;
  user?: PostHogAnalyticsUser;
  organization?: PostHogAnalyticsOrganization;
};

const useEmitPostHogEvents = ({
  appName,
  user,
  organization,
}: UseEmitPostHogEventsArgs = {}) => {
  const userEmail = user?.email;
  const orgId = organization?.id;
  const orgName = organization?.name;

  useEffect(() => {
    const token = resolvePostHogToken();
    if (!isPostHogInitEnabled || !token) {
      return;
    }
    posthog.init(token, {
      api_host: `${window.location.origin}/ph`,
      ui_host: 'https://eu.posthog.com',
      autocapture: false,
      capture_pageview: false,
      person_profiles: 'identified_only',
      // Web Experiments are disabled by default in posthog-js. Enable them in
      // every environment so they run for real users in production and can be
      // authored / previewed from the toolbar in non-prod.
      disable_web_experiments: false,
      // Outside production PostHog is initialized only so the toolbar can load
      // and preview flags / experiments / surveys. Opt out of all event capture
      // at the library level so dev / staging / localhost never send analytics
      // (custom events, autocapture, exceptions, survey responses, etc.). This
      // does NOT block the toolbar from loading/displaying those features.
      ...(isPostHogEnabled
        ? {}
        : {
            capture_pageleave: false,
            // Session replay has no toolbar-preview value and would record real
            // sessions; keep it off. Surveys are intentionally NOT disabled so
            // they can be previewed from the toolbar (responses stay unsent via
            // the opt-out below).
            disable_session_recording: true,
            opt_out_capturing_by_default: true,
          }),
    });
    // posthog-js's ESM build does not attach the instance to window. Expose it
    // so the PostHog toolbar can be launched from the browser console
    // (window.posthog.loadToolbar(...)) in every environment.
    window.posthog = posthog;
    if (!isPostHogEnabled) {
      // Belt-and-suspenders alongside opt_out_capturing_by_default: guarantees
      // no events leave non-prod even if a stored opt-in preference exists or a
      // new capture call site (e.g. OpsPilot's direct posthog.capture) is added
      // without an isPostHogEnabled guard.
      posthog.opt_out_capturing();
    }
  }, []);

  useEffect(() => {
    if (!isPostHogEnabled || !userEmail || !orgId || !orgName) {
      removeListener(listenerId);
      return;
    }

    const prevOrgId = posthog.getGroups()?.organization;
    const orgChanged = prevOrgId !== orgId;

    if (prevOrgId && orgChanged) {
      posthog.reset();
    }

    posthog.identify(userEmail);
    posthog.register({
      email: userEmail,
      environment: window.env.VITE_ENV,
      ...(appName ? { app: appName } : {}),
    });

    if (orgChanged) {
      posthog.group('organization', orgId, { orgID: orgId, orgName });
    }

    addListener(listenerId, (title, attributes) => {
      posthog.capture(title, attributes);
    });

    return () => removeListener(listenerId);
  }, [appName, userEmail, orgId, orgName]);
};

export const PostHogAnalytics = (props: UseEmitPostHogEventsArgs) => {
  useEmitPostHogEvents(props);
  return null;
};
